import { PropsOf, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { CountryCode } from 'libphonenumber-js'
import {
  ChangeEvent,
  forwardRef,
  Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import PhoneInput from 'react-phone-number-input/input'
import { formatSellmonitorPhone } from 'lib/phone'
import Input from './Input'

type InputComponent = 'input' | typeof Input

type Props = Omit<PropsOf<typeof Input>, 'defaultValue' | 'onChange' | 'value'> & {
  as?: InputComponent
  defaultValue?: string | null
  disablePhoneNormalization?: boolean
  hasError?: boolean
  onChange?: (value: string | null) => void
  value?: string | null
}

const assignRef = (targetRef: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null) => {
  if (typeof targetRef === 'function') {
    targetRef(node)
    return
  }

  if (targetRef) {
    const mutableRef = targetRef as { current: HTMLInputElement | null }
    mutableRef.current = node
  }
}

export const getFlagEmoji = (countryCode: CountryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

const PhoneNumberInput = (
  {
    as,
    defaultValue = null,
    disablePhoneNormalization,
    hasError,
    onChange,
    value,
    className,
    ...rest
  }: Props,
  ref: Ref<HTMLInputElement>
) => {
  const ctx = useUserContext()
  const Component = as ?? Input
  const inputRef = useRef<HTMLInputElement | null>(null)
  const shouldRestoreFocusRef = useRef(false)
  const [innerValue, setInnerValue] = useState<string | null>(defaultValue)
  const isControlled = value !== undefined
  const currentValue = isControlled ? value : innerValue
  const rawInput = !disablePhoneNormalization && !currentValue?.startsWith('+')

  useLayoutEffect(() => {
    if (!shouldRestoreFocusRef.current) {
      return
    }

    shouldRestoreFocusRef.current = false
    inputRef.current?.focus()
  }, [rawInput])

  return rawInput ? (
    <Component
      ref={(node: HTMLInputElement | null) => {
        inputRef.current = node
        assignRef(ref, node)
      }}
      {...(Component === Input ? { hasError } : {})}
      value={currentValue ?? ''}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = formatSellmonitorPhone(event.target.value, ctx.theme.hostType)
        const nextRawInput = !disablePhoneNormalization && !nextValue?.startsWith('+')

        if (nextRawInput !== rawInput) {
          shouldRestoreFocusRef.current = true
        }

        if (!isControlled) {
          setInnerValue(nextValue)
        }

        onChange?.(nextValue)
      }}
      autoComplete="tel"
      className={Component === 'input' ? c`disabled:cursor-not-allowed ${className}` : className}
      {...rest}
    />
  ) : (
    <PhoneInput
      ref={(node) => {
        inputRef.current = node as HTMLInputElement | null
        assignRef(ref, node as HTMLInputElement | null)
      }}
      inputComponent={Component as any}
      {...(Component === Input ? { hasError } : {})}
      value={currentValue ?? undefined}
      onChange={(nextValue) => {
        const normalizedValue = disablePhoneNormalization
          ? nextValue || null
          : formatSellmonitorPhone(nextValue, ctx.theme.hostType)
        const nextRawInput = !disablePhoneNormalization && !normalizedValue?.startsWith('+')

        if (nextRawInput !== rawInput) {
          shouldRestoreFocusRef.current = true
        }

        if (!isControlled) {
          setInnerValue(normalizedValue)
        }

        onChange?.(normalizedValue)
      }}
      autoComplete="tel"
      className={Component === 'input' ? c`disabled:cursor-not-allowed ${className}` : className}
      {...rest}
    />
  )
}

export default forwardRef(PhoneNumberInput) as typeof PhoneNumberInput
